// apps/web/src/features/clients/components/ContactFormModal.tsx
//
// Add OR edit a `ClientContact`. The create-shape schema is reused for
// edit; the PATCH payload is reduced to dirty fields client-side so the
// backend's same-value PATCH no-op stays meaningful.
//
// Child-modal contract (Decision 21):
//   - Guards its OWN dirty local form while open.
//   - A successful submit commits directly via its own mutation and does NOT
//     make the parent ClientDetailPage / ClientEditModal dirty — onClose is
//     called directly (not via a parent dirty-bubble path).
//
// INVARIANT (AC-8/AC-9): a rejected save keeps every typed value. A duplicate
// phone is reported ON the phone field (and focused), not as a generic toast.
//
// WHY local dirty state: PhoneInput (react-international-phone) fires
// onChange with the dial code on mount ("+7" for RU) when value="", which
// would make formState.isDirty=true before user interaction in add mode.
// The watch() subscription is registered after child effects run, so it
// never catches that library-initiated call. See ClientCreateModal.tsx.
import { useCallback, useEffect, useId, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  CLIENT_CONTACT_COMMENT_MAX_LENGTH,
  CLIENT_CONTACT_FULL_NAME_MAX_LENGTH,
  ClientContactInputSchema,
  type ClientContact,
  type ClientContactInput,
  type PatchClientContactInput,
} from '@magimp/shared';
import { useAddClientContact, usePatchClientContact } from '@magimp/shared-hooks';
import {
  Banner,
  Button,
  EditorModal,
  FormRow,
  Input,
  PhoneInput,
  Textarea,
} from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { phoneError } from '../../../shared/index.js';
import { useClientFieldError } from '../useClientFieldError.js';
import { useClientSubmitError } from '../useClientSubmitError.js';
import { useMobileFieldStyle, useTouchActionStyle, useTouchInput } from '../useMobileFieldStyle.js';
import { EditorCancelButton } from './EditorCancelButton.js';
import { FieldLengthHint } from './FieldLengthHint.js';

export interface ContactFormModalProps {
  clientId: string;
  /** Owning client, named in the title so the target record is unambiguous. */
  clientName: string;
  contact?: ClientContact;
  onClose: () => void;
}

type Form = { fullName: string; phone: string; comment: string };

export function ContactFormModal({
  clientId,
  clientName,
  contact,
  onClose,
}: ContactFormModalProps) {
  const { t } = useTranslation(['clients', 'common']);
  const showToast = useToastStore((s) => s.show);
  const add = useAddClientContact(clientId);
  const patch = usePatchClientContact(clientId, contact?.id ?? '__none__');
  const submitError = useClientSubmitError();
  const fieldError = useClientFieldError();
  const fieldStyle = useMobileFieldStyle();
  const actionStyle = useTouchActionStyle();
  const touchInput = useTouchInput();
  const counterId = useId();
  const formId = useId();

  const [dirty, setDirty] = useState(false);

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<Form>({
    resolver: zodResolver(ClientContactInputSchema as never),
    defaultValues: {
      fullName: contact?.fullName ?? '',
      phone: contact?.phone ?? '',
      comment: contact?.comment ?? '',
    },
  });

  const pending = add.isPending || patch.isPending;
  const isEdit = !!contact;
  const commentLength = (watch('comment') ?? '').length;

  // Subscribe via watch() to track user-initiated changes. The subscription
  // is registered in a parent-component effect — after PhoneInput's child
  // effects have already fired — so the dial-code init call (in add mode)
  // is never caught. See WHY comment in file header.
  useEffect(() => {
    const subscription = watch(() => setDirty(true));
    return () => subscription.unsubscribe();
  }, [watch]);

  // Reset dirty on unmount so the provider has no stale signal.
  useEffect(() => () => setDirty(false), []);

  const resetDirty = useCallback(() => setDirty(false), []);

  const onError = (err: unknown) =>
    submitError.report(err, (field, message) => {
      if (field === 'phone') setError('phone', { message }, { shouldFocus: true });
      if (field === 'name') setError('fullName', { message }, { shouldFocus: true });
    });

  const onSubmit = (values: Form) => {
    submitError.clear();
    if (isEdit) {
      const payload: PatchClientContactInput = {};
      if (dirtyFields.fullName) payload.fullName = values.fullName;
      if (dirtyFields.phone) payload.phone = values.phone;
      if (dirtyFields.comment) payload.comment = values.comment === '' ? null : values.comment;
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }
      patch.mutate(payload, {
        onSuccess: () => {
          // Reset dirty then close directly (not via guard requestClose) so
          // the parent does not see a dirty signal from this child.
          resetDirty();
          showToast({ tone: 'success', text: t('clients:toasts.contactSaved') });
          onClose();
        },
        onError,
      });
    } else {
      const payload: ClientContactInput = {
        fullName: values.fullName,
        phone: values.phone,
        comment: values.comment === '' ? null : values.comment,
      };
      add.mutate(payload, {
        onSuccess: () => {
          // Reset dirty then close directly (not via guard requestClose) so
          // the parent does not see a dirty signal from this child.
          resetDirty();
          showToast({ tone: 'success', text: t('clients:toasts.contactAdded') });
          onClose();
        },
        onError,
      });
    }
  };

  return (
    <EditorModal
      open={true}
      title={
        isEdit
          ? t('clients:modals.editContactTitle', { client: clientName })
          : t('clients:modals.addContactTitle', { client: clientName })
      }
      width={480}
      narrowFullWidth
      dirty={dirty}
      busy={pending}
      onConfirmClose={onClose}
      footer={
        <>
          <EditorCancelButton
            onClose={onClose}
            label={t('common:actions.cancel')}
            style={actionStyle}
          />
          <Button
            type="submit"
            form={formId}
            kind="primary"
            style={actionStyle}
            disabled={pending}
            data-testid="client-contact-submit"
          >
            {isEdit ? t('common:actions.save') : t('common:actions.create')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        {submitError.state.text !== null && (
          <div style={{ marginBottom: 'var(--s-3)' }}>
            <Banner tone={submitError.state.uncertain ? 'warn' : 'error'}>
              {submitError.state.text}
            </Banner>
          </div>
        )}

        <FormRow
          label={t('clients:fields.contactFullName')}
          error={fieldError(errors.fullName, {
            kind: 'text',
            max: CLIENT_CONTACT_FULL_NAME_MAX_LENGTH,
          })}
        >
          <Controller
            control={control}
            name="fullName"
            render={({ field }) => (
              <Input
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                invalid={!!errors.fullName}
                maxLength={CLIENT_CONTACT_FULL_NAME_MAX_LENGTH}
                style={fieldStyle}
              />
            )}
          />
        </FormRow>
        <FormRow
          label={t('clients:fields.phone')}
          hint={t('clients:hints.phoneFormat')}
          error={phoneError(errors.phone?.message, t('clients:errors.invalidPhone'))}
        >
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.phone}
                testId="client-contact-phone"
                style={fieldStyle}
                // Same touch presentation as the client-create phone field.
                touchTargets={touchInput}
              />
            )}
          />
        </FormRow>
        <FormRow
          label={t('clients:fields.contactComment')}
          error={fieldError(errors.comment, {
            kind: 'text',
            max: CLIENT_CONTACT_COMMENT_MAX_LENGTH,
          })}
        >
          <Controller
            control={control}
            name="comment"
            render={({ field }) => (
              <Textarea
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                invalid={!!errors.comment}
                ariaDescribedBy={counterId}
                style={fieldStyle}
              />
            )}
          />
        </FormRow>
        <FieldLengthHint
          id={counterId}
          length={commentLength}
          max={CLIENT_CONTACT_COMMENT_MAX_LENGTH}
        />
      </form>
    </EditorModal>
  );
}
